import { useEffect, useState } from "react";
import api, { formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
  LineChart, Line
} from "recharts";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, d] = await Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/driver-performance")]);
      setStats(s.data); setDrivers(d.data);
    })();
  }, []);

  if (!stats) return <div className="p-8 text-neutral-500">Yükleniyor...</div>;
  const monthly = (stats.monthly || []).map(m => ({ ...m, label: monthLabel(m.month) }));

  return (
    <>
      <PageHeader
        title="Raporlar"
        subtitle="Gelir & gider analizleri, kategori bazlı harcamalar ve şoför performansı."
        testid="reports-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Aylık Gelir Trend</div>
          <div className="text-lg font-bold font-heading mt-1 mb-4">Gelirler</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                <Tooltip formatter={(v) => formatTL(v)} />
                <Line type="monotone" dataKey="income" name="Gelir" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Kategori Bazlı Aylık Gider</div>
          <div className="text-lg font-bold font-heading mt-1 mb-4">Gider Analizi</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                <Tooltip formatter={(v) => formatTL(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="fuel" stackId="a" fill="#DC2626" name="Yakıt" />
                <Bar dataKey="salary" stackId="a" fill="#0020C2" name="Maaş" />
                <Bar dataKey="supplier" stackId="a" fill="#F59E0B" name="Tedarikçi" />
                <Bar dataKey="invoice" stackId="a" fill="#0A0A0A" name="Fatura" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="report-driver-perf">
        <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Şoför Performansı — Detaylı</div>
        <div className="text-lg font-bold font-heading mt-1 mb-4">Km & Yakıt Verimliliği</div>
        {drivers.length === 0 ? (
          <div className="text-sm text-neutral-500">Henüz veri yok</div>
        ) : (
          <div style={{ width: "100%", height: Math.max(280, drivers.length * 40) }}>
            <ResponsiveContainer>
              <BarChart data={drivers} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(v, k) => k === "avg_consumption" ? `${formatNumber(v, 2)} L/100km` : formatNumber(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="total_km" name="Toplam Km" fill="#0020C2" />
                <Bar dataKey="avg_consumption" name="Ort. L/100km" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </>
  );
}
